// filepath: c:\Users\gianq\Documents\contactaMe\Frontend\contactame_app\src\utils\errorLogger.js
// errorLogger.js
import React from "react";
import { auth } from "../firebase";
import { uploadJson } from "../storageService";

/**
 * Sube un log de error de cliente a Firebase Storage.
 * - Guarda en: error-logs/{YYYY-MM-DD}/{uid|anon}/{timestamp}__{route}.json
 * - Sube incluso sin autenticación (irá a la carpeta anon)
 */
export async function logClientErrorToStorage(error, errorInfo, extra = {}) {
  try {
    const VERBOSE = process.env.REACT_APP_ERRORLOG_VERBOSE === '1' || process.env.REACT_APP_ERRORLOG_VERBOSE === 'true';
    // Datos de contexto
    const user = auth?.currentUser || null;
    const uid = user?.uid || "anon";
    const email = user?.email || null;

    const now = new Date();
    const iso = now.toISOString();
    const epoch = now.getTime();

    const locationHref = typeof window !== "undefined" ? window.location.href : "";
    const pathname = typeof window !== "undefined" ? window.location.pathname : "";
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const language = typeof navigator !== "undefined" ? navigator.language : "";
    const platform = typeof navigator !== "undefined" ? navigator.platform : "";

    // IDs de correlación (sesión y carga de página)
    let sessionId = null;
    let pageLoadId = null;
    try {
      sessionId = sessionStorage.getItem('sessionId');
      if (!sessionId) {
        sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem('sessionId', sessionId);
      }
      pageLoadId = window.__PAGE_LOAD_ID || (Math.random().toString(36).slice(2));
      window.__PAGE_LOAD_ID = pageLoadId;
    } catch (_) {}

    // Info de red (curada)
    let network = null;
    try {
      const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      network = {
        onLine: navigator.onLine,
        effectiveType: c?.effectiveType || null,
        saveData: c?.saveData || null,
      };
    } catch (_) {}

    // Info de dispositivo (solo si VERBOSE)
    let device = null;
    if (VERBOSE) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
        const reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
        device = {
          hardwareConcurrency: navigator.hardwareConcurrency || null,
          deviceMemory: navigator.deviceMemory || null,
          timezone: tz,
          prefersReducedMotion: !!reduceMotion,
        };
      } catch (_) {}
    }

    // Resumen de recursos externos (top hosts) – mantener porque ayuda a detectar terceros
    let externalResources = null;
    try {
      const hosts = {};
      const pushHost = (url) => {
        try {
          if (!url) return;
          const u = new URL(url, locationHref);
          if (!u.host) return;
          hosts[u.host] = (hosts[u.host] || 0) + 1;
        } catch (_) {}
      };
      document.querySelectorAll('script[src]').forEach(s => pushHost(s.getAttribute('src')));
      document.querySelectorAll('link[href]').forEach(l => pushHost(l.getAttribute('href')));
      document.querySelectorAll('iframe[src]').forEach(i => pushHost(i.getAttribute('src')));
      const topHosts = Object.entries(hosts).sort((a,b) => b[1] - a[1]).slice(0,5).map(([host,count]) => ({ host, count }));
      externalResources = { topHosts, totalHosts: Object.keys(hosts).length };
    } catch (_) {}

    // Señal de anuncios (clave)
    let adsSummary = null;
    try {
      const count = (sel) => document.querySelectorAll(sel).length;
      adsSummary = {
        insAdsbygoogle: count('ins.adsbygoogle'),
        googleAdsIds: count('[id^="google_ads"]'),
        aswiftIds: count('[id^="aswift_"]'),
        googleAdsIframes: count('iframe[src*="googleads"]'),
      };
    } catch (_) {}

    // Señal de DOM guard
    let domGuard = undefined;
    try {
      if (typeof window !== 'undefined' && window.__DOM_GUARD_ACTIVE) {
        const logs = Array.isArray(window.__DOM_PATCH_LOGS) ? window.__DOM_PATCH_LOGS.slice(-10) : [];
        domGuard = { active: true, logsCount: logs.length, logs };
      }
    } catch (_) {}

    // Timings de paint – solo si VERBOSE
    let paint = null;
    if (VERBOSE) {
      try {
        const paints = performance.getEntriesByType ? performance.getEntriesByType('paint') : null;
        if (paints && paints.length) {
          paint = {};
          paints.forEach(p => { paint[p.name] = p.startTime; });
        }
      } catch (_) {}
    }

    // Service Worker (útil si hay caché agresiva)
    let sw = null;
    try {
      sw = { controlled: !!navigator.serviceWorker?.controller };
    } catch (_) {}

    // Sanitizador de anuncios: incluir conteo global si existe
    let sanitizer = undefined;
    try {
      const prunedAdsCount = (window.__SANITIZER_PRUNED_ADS || 0);
      if (prunedAdsCount > 0) sanitizer = { prunedAdsCount };
    } catch (_) {}

    // Breadcrumbs y API trail si existen (los puede poblar la app)
    let breadcrumbs = undefined;
    let apiTrail = undefined;
    try {
      if (Array.isArray(window.__BREADCRUMBS) && window.__BREADCRUMBS.length) breadcrumbs = window.__BREADCRUMBS.slice(-20);
      if (Array.isArray(window.__API_TRAIL) && window.__API_TRAIL.length) apiTrail = window.__API_TRAIL.slice(-10);
    } catch (_) {}

    // Visibilidad de documento
    let visibility = null;
    try {
      visibility = { visibilityState: document.visibilityState, hidden: document.hidden };
    } catch (_) {}

    // Información sobre el estado del DOM
    let domInfo;
    try {
      // Obtener información sobre el elemento activo
      const activeElement = document.activeElement?.tagName;
      const activeElementId = document.activeElement?.id;
      const activeElementClasses = document.activeElement?.className;

      // Recolectar información sobre elementos en foco
      const focusedElements = Array.from(document.querySelectorAll(':focus'))
        .map(el => ({
          tagName: el.tagName,
          id: el.id,
          className: el.className
        }));

      // Conteo de elementos por tipo para detectar posibles problemas de rendimiento
      const elementCounts = {};
      document.querySelectorAll('*').forEach(el => {
        const tag = el.tagName.toLowerCase();
        elementCounts[tag] = (elementCounts[tag] || 0) + 1;
      });

      domInfo = {
        activeElement,
        activeElementId,
        activeElementClasses,
        focusedElements,
        elementCounts,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        scrollPosition: {
          x: window.scrollX,
          y: window.scrollY
        }
      };
    } catch (e) {
      domInfo = { error: e.message };
    }

    // Recoger información sobre la navegación del usuario
    let userNavigation;
    try {
      const history = window.history;
      userNavigation = {
        referrer: document.referrer,
        historyLength: history?.length || 0,
        currentStep: extra?.currentStep,
        previousURL: sessionStorage.getItem('previousURL')
      };
      // Guardar URL actual para futuras referencias
      sessionStorage.setItem('previousURL', locationHref);
    } catch (_) {
      userNavigation = null;
    }

    // Información detallada sobre errores DOM y estado de React
    let domErrorInfo;
    try {
      // Verificar si el error está relacionado con manipulación DOM
      const isDomError = error?.message?.includes('removeChild') ||
                         error?.message?.includes('insertBefore') ||
                         error?.message?.includes('appendChild') ||
                         error?.message?.includes('replaceChild');

      // Si es un error DOM, recopilar información adicional
      if (isDomError) {
        // Revisar los componentes que tienen problemas con fibers desconectados
        let fiberInfo = null;
        try {
          const reactInstances = Array.from(document.querySelectorAll('[data-reactroot]'))
            .map(node => {
              const key = Object.keys(node).find(key => key.startsWith('__reactFiber$'));
              return key ? node[key] : null;
            })
            .filter(Boolean);

          if (reactInstances.length) {
            fiberInfo = {
              fiberCount: reactInstances.length,
              componentNames: reactInstances.map(fiber => fiber.type?.displayName || fiber.type?.name || 'Unknown').slice(0, 5)
            };
          }
        } catch (e) {
          fiberInfo = { error: e.message };
        }

        domErrorInfo = {
          isDomError,
          errorType: isDomError ? 'DOM_MUTATION_ERROR' : 'OTHER',
          renderingPhase: document.readyState,
          fiberInfo,
          unmountedComponents: extra?.unmountedComponents || [],
          componentUpdateQueue: extra?.updateQueue || null
        };
      }
    } catch (e) {
      domErrorInfo = { error: e.message };
    }

    // Recoger información sobre timers activos
    let asyncState;
    try {
      // Información aproximada sobre timers (no podemos acceder a los timers internos)
      const pendingTimeouts = extra?.pendingTimeouts || 'unknown';
      const pendingAnimationFrames = extra?.pendingAnimationFrames || 'unknown';

      asyncState = {
        pendingTimeouts,
        pendingAnimationFrames,
        documentReadyState: document.readyState,
        isPageLoaded: document.readyState === 'complete'
      };
    } catch (_) {
      asyncState = null;
    }

    const reactVersion = React?.version || null;
    const appVersion = process.env.REACT_APP_VERSION || null;

    // Tomar ubicación seleccionada desde localStorage si existe (para payload)
    let selectedLocation = null;
    try {
      const sl = localStorage.getItem('selectedLocation');
      selectedLocation = sl ? JSON.parse(sl) : null;
    } catch (_) {
      selectedLocation = null;
    }

    // Memoria JS del navegador (si disponible) para payload
    let memory = null;
    try {
      const perf = window?.performance;
      if (perf && perf.memory) {
        const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = perf.memory;
        memory = { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize };
      }
    } catch (_) {
      memory = null;
    }

    const payload = {
      kind: "client-error",
      timestamp: iso,
      epoch,
      route: pathname,
      href: locationHref,
      user: { uid, email, isAuthenticated: !!user },
      environment: {
        userAgent,
        language,
        platform,
        memory,
        reactVersion,
        appVersion,
        network,
        ...(VERBOSE ? { device } : {}),
        ids: { sessionId, pageLoadId }
      },
      locationSelection: selectedLocation,
      error: {
        name: error?.name || null,
        message: error?.message || String(error),
        stack: error?.stack || null,
      },
      react: {
        componentStack: errorInfo?.componentStack || null,
      },
      dom: { ...domInfo, adsSummary },
      domError: domErrorInfo,
      navigation: { ...userNavigation, visibility },
      asyncState: asyncState,
      extra: { ...(extra || {}), resources: externalResources, ...(VERBOSE ? { paint } : {}), sw, ...(sanitizer ? { sanitizer } : {}), ...(breadcrumbs ? { breadcrumbs } : {}), ...(apiTrail ? { apiTrail } : {}), ...(domGuard ? { domGuard } : {}) },
    };

    // Construir path YYYY-MM-DD
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const dayFolder = `${yyyy}-${mm}-${dd}`;

    // Sanitizar nombre del archivo con el pathname
    const safeRoute = pathname
      ? pathname.replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 80)
      : "root";

    const fileName = `${epoch}__${safeRoute}.json`;
    // La estructura correcta: error-logs/YYYY-MM-DD/uid/fileName
    const path = `error-logs/${dayFolder}/${uid}/${fileName}`;

    try {
      // Subir como JSON (aunque no haya auth; si falla por red, encola)
      await uploadJson(payload, path);
      console.log(`✅ Log de error guardado correctamente en: ${path}`);
      return { ok: true, path };
    } catch (uploadError) {
      console.warn("❌ No se pudo subir el log de error a Storage:", uploadError?.message || uploadError);
      return { ok: false, error: uploadError };
    }
  } catch (e) {
    // Evitar que un fallo aquí rompa la app; registrar en consola
    console.warn("No se pudo subir el log de error a Storage:", e?.message || e);
    return { ok: false, error: e };
  }
}
